import iconNodes from 'lucide-static/icon-nodes.json';
import type { IconAttributeValue, IconNode } from '@source-components-react/types';
import { resolveIconName } from './resolve-icon-name';

const registry = iconNodes as Record<string, IconNode>;

/**
 * Geometry lookup for a persisted icon name.
 *
 * Registry-side only: the render path never calls this. Published payloads
 * carry geometry inline on the attribute (see `buildIconAttributeValue`), so
 * customer pages never load this module or the ~722 KB of JSON behind it.
 */
export const resolveIconNode = (name: unknown): IconNode | null => {
  const resolvedName = resolveIconName(name);
  if (!resolvedName) return null;
  return registry[resolvedName] ?? null;
};

/**
 * Build the value the composer persists when an author picks an icon.
 *
 * Geometry is stamped at selection rather than at publish so that every
 * surface — composer canvas, dev-environment host apps, staging and
 * production — renders from the same attribute with no registry present.
 * `name` travels alongside so a later sync can re-resolve stale geometry.
 */
export const buildIconAttributeValue = (name: unknown): IconAttributeValue | null => {
  const resolvedName = resolveIconName(name);
  if (!resolvedName) return null;

  const iconNode = registry[resolvedName];
  if (!iconNode) return null;

  return { name: resolvedName, iconNode };
};

export const isKnownIconName = (name: unknown): boolean => {
  const resolved = resolveIconName(name);
  return resolved != null && resolved in registry;
};

export const listIconNames = (): string[] => Object.keys(registry);
